import { pageTitle } from '@palaccount/config';
import type { StockLevelRow, StockStatus } from '@palaccount/contracts';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Amount, Ltr, Qty } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Typeahead } from '@/components/ui/typeahead';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { formatQty } from '@/lib/format';
import { localizedName } from '@/lib/localized-name';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import { useItemCategories, useManufacturers, useUnits } from './api';
import { InventoryTabs } from './inventory-tabs';
import { Chip, MovementKindPill, StockStatusBadge } from './stock-chips';
import { useItemMovements, useStockLevels, useWarehouses } from './stock-api';
import { availableClass, signedQtyClass, signedQtyWithUnit, stockRowWash } from './stock-format';
import { Modal } from './modal';

const STATUS_FILTERS = ['low', 'out', 'over'] as const;

/**
 * BR-INV-05-08 — §5.2 field 13's three ADDED filters, each "a removable chip
 * with its own value picker", and each of which "must actually filter the
 * rows". The i18n key beside each is the chip's own name; the same three keys
 * feed the Add-filter list, the picker labels and the remove buttons, so no
 * two of them can drift apart.
 */
const ADDED_FILTERS = ['supplier', 'tax', 'uom'] as const;
type AddedFilter = (typeof ADDED_FILTERS)[number];
const ADDED_FILTER_LABEL: Record<AddedFilter, string> = {
  supplier: 'stock.filterSupplier',
  tax: 'stock.filterTaxCategory',
  uom: 'stock.filterUom',
};

/** BR-INV-05-10's history modal — a dialog, so its block is smaller than a screen's. */
const HISTORY_PAGE_SIZE = 25;

/** BR-INV-05-10 — the clock icon: this ITEM's ledger, nothing else's. */
function HistoryModal({
  itemId,
  itemName,
  onClose,
}: {
  itemId: string;
  itemName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  /*
   * NFR-42 — one page of one item's history. Component state rather than the
   * URL, deliberately: a MODAL is not a linkable location — closing it drops
   * the page as it drops the modal, and `?page=` here would collide with the
   * list's own page behind it.
   */
  const [page, setPage] = useState(1);
  const { data, isPending } = useItemMovements(itemId, page, HISTORY_PAGE_SIZE);
  const rows = data?.rows;
  const range = usePageRange(data, HISTORY_PAGE_SIZE);
  return (
    <Modal title={`${t('stock.historyTitle')} — ${itemName}`} onClose={onClose} className="max-w-3xl">
      {isPending ? (
        <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
      ) : (rows ?? []).length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('stock.noMovements')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('mv.colType')}</TableHead>
              <TableHead>{t('mv.colReference')}</TableHead>
              <TableHead>{t('mv.colWarehouse')}</TableHead>
              <TableHead>{t('common.date')}</TableHead>
              <TableHead className="text-end">{t('mv.colQty')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(rows ?? []).map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <MovementKindPill kind={row.kind} />
                </TableCell>
                <TableCell>
                  <Ltr value={row.referenceNo} className="font-mono text-sm" />
                </TableCell>
                <TableCell className="text-sm">{row.warehouseName}</TableCell>
                <TableCell>
                  <Ltr value={row.occurredAt.slice(0, 10)} className="font-mono text-sm" />
                </TableCell>
                <TableCell className={cn('text-end font-mono font-semibold', signedQtyClass(row.qty))}>
                  {/* N-4/N-14: the sign, the unit's decimals, the unit — in ONE
                      LTR island so the pair cannot come apart in Arabic. */}
                  <Ltr value={signedQtyWithUnit(row.qty, row.unitPrecision, row.unitSymbol)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {(data?.total ?? 0) > 0 ? (
        <Pager
          page={page}
          pages={range.pages}
          first={range.first}
          last={range.last}
          total={range.total}
          onPage={setPage}
          label={t('stock.historyTitle')}
        />
      ) : null}
    </Modal>
  );
}

/** The expander's contents — one card per location holding this item. */
function Breakdown({ row }: { row: StockLevelRow }) {
  const { t } = useTranslation();
  return (
    <section aria-label={t('stock.breakdown')} className="grid gap-3 bg-muted/30 p-4">
      <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {t('stock.breakdown')}
      </h4>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {row.warehouses.map((warehouse) => (
          <Card key={warehouse.warehouseId}>
            <CardContent className="grid gap-2 py-4">
              <span className="text-sm font-medium">{warehouse.warehouseName}</span>
              <dl className="grid grid-cols-3 gap-2 text-xs">
                <div className="grid gap-0.5">
                  <dt className="text-muted-foreground">{t('stock.colOnHand')}</dt>
                  <dd>
                    <Qty
                      value={warehouse.onHand}
                      precision={row.unitPrecision}
                      unit={row.unitSymbol}
                      className="font-semibold"
                    />
                  </dd>
                </div>
                <div className="grid gap-0.5">
                  <dt className="text-muted-foreground">{t('stock.colReserved')}</dt>
                  <dd>
                    <Qty value={warehouse.reserved} precision={row.unitPrecision} unit={row.unitSymbol} />
                  </dd>
                </div>
                <div className="grid gap-0.5">
                  <dt className="text-muted-foreground">{t('stock.colAvailable')}</dt>
                  <dd>
                    <Qty
                      value={warehouse.available}
                      precision={row.unitPrecision}
                      unit={row.unitSymbol}
                      className="font-semibold"
                    />
                  </dd>
                </div>
              </dl>
              {/*
                BR-INV-05-11 — §5.5's two strips, one chip per bin / per lot,
                shown only where the item HAS them ("an item may show bins,
                batches, both or neither"). The quantities are the server's
                ledger sums — Σ movements partitioned by bin and by lot, the
                same rows whose total is the on-hand above — so the chips
                cannot disagree with the figure they sit under, which is the
                whole rule. N-4: the unit's own decimals, in one LTR island
                per chip so code and quantity cannot come apart in Arabic.
              */}
              {(warehouse.bins ?? []).length > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">{t('stock.bins')}</span>
                  {warehouse.bins.map((bin) => (
                    <span
                      key={bin.binId}
                      className="rounded-full border px-2 py-0.5 font-mono text-xs"
                    >
                      <Ltr value={`${bin.binCode} · ${formatQty(bin.qty, row.unitPrecision)}`} />
                    </span>
                  ))}
                </div>
              ) : null}
              {(warehouse.batches ?? []).length > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">{t('stock.batches')}</span>
                  {warehouse.batches.map((batch) => (
                    <span
                      key={batch.batchNo}
                      className="rounded-full border px-2 py-0.5 font-mono text-xs"
                    >
                      <Ltr
                        value={`${batch.batchNo} · ${formatQty(batch.qty, row.unitPrecision)}`}
                      />
                    </span>
                  ))}
                </div>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

/**
 * INV-05 — stock balances. The five cards are the SERVER's unfiltered figures
 * (BR-INV-05-05: they answer "how is the business doing", never "what does
 * this filter show"). The status badge follows BR-INV-05-03 — an OK row says
 * nothing at all — and the row wash beats the zebra stripe.
 */
export function StockLevelsPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t, i18n } = useTranslation();

  // ADM-04's aggregate stock notifications land here (BR-ADM-04-06): the
  // sentence is about the low-stock LIST, per EC-15-12, so the link carries
  // `?filter=low` rather than an item id. Seeded once, then ordinary state.
  // `?q=` is the other deep link — the Items list's on-hand figure opens
  // Stock Levels searched to that item's code (bugs: Items list).
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(() => searchParams.get('q') ?? '');
  const [status, setStatus] = useState<StockStatus | ''>(() => {
    const wanted = searchParams.get('filter');
    return wanted === 'low' || wanted === 'out' || wanted === 'over' || wanted === 'ok'
      ? wanted
      : '';
  });
  const [warehouseIds, setWarehouseIds] = useState<string[]>([]);
  const [categoryId, setCategoryId] = useState('');
  /** bugs: ST-02 — the manufacturer filter (IT-S6). */
  const [manufacturerId, setManufacturerId] = useState('');
  /** bugs: Stock levels — Tracked / Untracked on Track stock alerts (IT-L3). */
  const [alerts, setAlerts] = useState<'' | 'tracked' | 'untracked'>('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [moreFilters, setMoreFilters] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [history, setHistory] = useState<{ id: string; name: string } | null>(null);
  /**
   * BR-INV-05-08 — the three added filters (§5.2 field 13). The chip LIST is
   * plain state (which chips are on screen); the chip VALUES are filters, so
   * every value write below goes through `paging.change`. An added chip with
   * no value yet constrains nothing — the same convention as an empty chip
   * set (BR-INV-05-06).
   */
  const [addedFilters, setAddedFilters] = useState<AddedFilter[]>([]);
  const [supplierFilterId, setSupplierFilterId] = useState<string | null>(null);
  const [taxCategoryFilter, setTaxCategoryFilter] = useState('');
  const [unitFilterId, setUnitFilterId] = useState('');
  /**
   * NFR-42 — the page is in the URL; every filter resets it to 1.
   *
   * BR-INV-05-07 is that reset, and it holds by CONSTRUCTION rather than by
   * eight remembered calls: every filter on this screen — the added trio of
   * BR-INV-05-08 included — is set through `paging.change`, which is `setter`
   * + `setPage(1)` in one function, so a filter that forgets to reset the
   * pager is a filter that forgets to apply itself. The rule also names one
   * more control, the "Roll variants into parent" toggle — INV-05 §5.2 field
   * 12 — and that toggle IS NOT BUILT on this screen, so the rule is kept for
   * every filter that exists and its named exception has nothing to attach
   * to. Whoever builds rollup wires it through `paging.change` too — and the
   * clamp below (ERR-INV-05-03) catches whoever forgets.
   */
  const paging = usePagedState();

  useEffect(() => {
    document.title = pageTitle(t('stock.title'));
  }, [t]);

  const filters = {
    q: q.trim() || undefined,
    status: status || undefined,
    warehouseIds,
    categoryId: categoryId || undefined,
    manufacturerId: manufacturerId || undefined,
    alerts: alerts || undefined,
    includeInactive,
    // BR-INV-05-08: the added values ride the SAME request as everything
    // else. ERR-INV-05-02 was a chip whose value went nowhere; here the value
    // IS the query, so a rendered chip and an applied filter are one thing.
    supplierId: supplierFilterId ?? undefined,
    taxCategory: taxCategoryFilter.trim() || undefined,
    unitId: unitFilterId || undefined,
  };
  const { data, isPending, isError } = useStockLevels(filters, paging.page, paging.pageSize);
  const range = usePageRange(data, paging.pageSize);
  const { data: warehouses } = useWarehouses({ status: 'active' });
  const { data: categories } = useItemCategories(true);
  const { data: manufacturers } = useManufacturers(true);
  const { data: units } = useUnits();
  const supplierPicker = usePartnerPicker(supplierFilterId, { type: 'supplier' });

  const summary = data?.summary;
  const rows = useMemo(() => data?.rows ?? [], [data]);

  /*
   * ERR-INV-05-03 — "Turned off rollup on page 2 | The list may show nothing
   * until the page clamps | Go back to page 1." The §14 row is a hypothesis
   * about THIS build, and half of it held: the rollup toggle does not exist
   * and every filter resets the pager (BR-INV-05-07 above), but a page NUMBER
   * can still outlive the list it pointed into — a pasted `?page=9` link, or
   * data that shrank between visits — and the screen rested on "no items
   * match the current filters", which is a false sentence with the pager
   * hidden under it. So the screen does what the row says a correct build
   * does: it CLAMPS. When a settled response proves the page is past the end,
   * the pager drops to the last page that exists (page 1 when nothing does),
   * and the user is never left reading an empty page of a non-empty list.
   */
  const total = data?.total;
  useEffect(() => {
    if (isPending || total === undefined) return;
    const lastPage = Math.max(1, Math.ceil(total / paging.pageSize));
    if (paging.page > lastPage) paging.setPage(lastPage);
  }, [isPending, total, paging]);

  const toggleWarehouse = (id: string) =>
    paging.change(setWarehouseIds)(
      warehouseIds.includes(id)
        ? warehouseIds.filter((entry) => entry !== id)
        : [...warehouseIds, id],
    );

  const addFilter = (filter: AddedFilter) =>
    setAddedFilters((current) => (current.includes(filter) ? current : [...current, filter]));

  /**
   * Removing a chip clears its value THROUGH `paging.change`: a chip whose
   * value was narrowing the list changes the row set when it goes, so the
   * removal is a filter change like any other (BR-INV-05-07).
   */
  const removeFilter = (filter: AddedFilter) => {
    setAddedFilters((current) => current.filter((entry) => entry !== filter));
    if (filter === 'supplier') paging.change(setSupplierFilterId)(null);
    if (filter === 'tax') paging.change(setTaxCategoryFilter)('');
    if (filter === 'uom') paging.change(setUnitFilterId)('');
  };

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('stock.title')}</h2>
        {/* No Stock Take button here (bugs: Stock Levels) — counts have their
            own tab, and a second door to them on this screen was noise. */}
      </div>

      {/* BR-INV-05-05 — computed over EVERYTHING; the filter bar never moves them. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('stock.cardItems')}</span>
            <span className="font-mono text-2xl font-bold">{summary?.itemsTracked ?? '—'}</span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('stock.cardValue')}</span>
            {summary ? (
              <Amount value={summary.totalStockValue} currency={baseCurrency} className="text-2xl font-bold" />
            ) : (
              <span className="font-mono text-2xl font-bold">—</span>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('stock.cardLow')}</span>
            <span className="font-mono text-2xl font-bold text-amber-700 dark:text-amber-400">
              {summary?.lowCount ?? '—'}
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('stock.cardOut')}</span>
            <span className="font-mono text-2xl font-bold text-red-600 dark:text-red-400">
              {summary?.outCount ?? '—'}
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('stock.cardWarehouses')}</span>
            <span className="font-mono text-2xl font-bold">{summary?.warehouseCount ?? '—'}</span>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="grid gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-[320px]">
              <Search
                className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                aria-label={t('stock.searchLabel')}
                placeholder={t('stock.searchPh')}
                value={q}
                onChange={(event) => paging.change(setQ)(event.target.value)}
                className="h-10 ps-9 text-sm"
              />
            </div>
            {/* Mutually exclusive — a row is one status (BR-INV-05-01). */}
            <div className="flex flex-wrap gap-2">
              <Chip active={status === ''} onClick={() => paging.change(setStatus)('')}>
                {t('stock.filterAll')}
              </Chip>
              {STATUS_FILTERS.map((option) => (
                <Chip
                  key={option}
                  active={status === option}
                  onClick={() => paging.change(setStatus)(status === option ? '' : option)}
                >
                  {t(`stock.filter${option.charAt(0).toUpperCase()}${option.slice(1)}`)}
                </Chip>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ms-auto"
              aria-expanded={moreFilters}
              onClick={() => setMoreFilters((current) => !current)}
            >
              <SlidersHorizontal aria-hidden /> {t('stock.moreFilters')}
            </Button>
          </div>

          {/* BR-INV-05-06: the location chips are ANY-OF; none on = no constraint. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('stock.filterWarehouses')}</span>
            {(warehouses ?? []).map((warehouse) => (
              <Chip
                key={warehouse.id}
                active={warehouseIds.includes(warehouse.id)}
                onClick={() => toggleWarehouse(warehouse.id)}
              >
                {warehouse.name}
              </Chip>
            ))}
          </div>

          {moreFilters ? (
            <div className="grid gap-3 rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-4">
                <NativeSelect
                  aria-label={t('stock.filterCategory')}
                  value={categoryId}
                  onChange={(event) => paging.change(setCategoryId)(event.target.value)}
                  className="h-10 w-auto text-sm"
                >
                  <option value="">{t('stock.filterCategoryAll')}</option>
                  {(categories ?? []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </NativeSelect>
                {/* bugs: ST-02 — one maker's items (IT-S6). */}
                <NativeSelect
                  aria-label={t('stock.filterManufacturer')}
                  value={manufacturerId}
                  onChange={(event) => paging.change(setManufacturerId)(event.target.value)}
                  className="h-10 w-auto text-sm"
                >
                  <option value="">{t('stock.filterManufacturerAll')}</option>
                  {(manufacturers ?? []).map((maker) => (
                    <option key={maker.id} value={maker.id}>
                      {localizedName(maker, i18n.language)}
                    </option>
                  ))}
                </NativeSelect>
                {/* IT-L3 on this screen too: an item whose stock "doesn't matter"
                    can be hidden from, or isolated on, the balances list. */}
                <NativeSelect
                  aria-label={t('stock.filterAlerts')}
                  value={alerts}
                  onChange={(event) => paging.change(setAlerts)(event.target.value as typeof alerts)}
                  className="h-10 w-auto text-sm"
                >
                  <option value="">{t('stock.filterAlertsAll')}</option>
                  <option value="tracked">{t('stock.filterAlertsTracked')}</option>
                  <option value="untracked">{t('stock.filterAlertsUntracked')}</option>
                </NativeSelect>
                <Label htmlFor="stock-inactive" className="flex items-center gap-2 font-normal">
                  <Checkbox
                    id="stock-inactive"
                    checked={includeInactive}
                    onCheckedChange={(checked) =>
                      paging.change(setIncludeInactive)(checked === true)
                    }
                  />
                  {t('common.showInactive')}
                </Label>
                {/*
                  BR-INV-05-08 — §6's Add filter: "Options already added
                  disappear from the list", so the select empties itself dry
                  and vanishes once all three chips are out. Adding a chip is
                  NOT a filter change (it starts empty and constrains
                  nothing); picking its value is, and does so below.
                */}
                {addedFilters.length < ADDED_FILTERS.length ? (
                  <NativeSelect
                    aria-label={t('stock.addFilter')}
                    value=""
                    onChange={(event) => {
                      const picked = event.target.value as AddedFilter | '';
                      if (picked !== '') addFilter(picked);
                    }}
                    className="h-10 w-auto text-sm"
                  >
                    <option value="">{t('stock.addFilter')}</option>
                    {ADDED_FILTERS.filter((filter) => !addedFilters.includes(filter)).map(
                      (filter) => (
                        <option key={filter} value={filter}>
                          {t(ADDED_FILTER_LABEL[filter])}
                        </option>
                      ),
                    )}
                  </NativeSelect>
                ) : null}
              </div>
              {addedFilters.map((filter) => (
                <div key={filter} className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {t(ADDED_FILTER_LABEL[filter])}
                  </span>
                  {filter === 'supplier' ? (
                    <div className="w-full max-w-[280px]">
                      {/* GEN-50: suppliers only, searched — the same picker as
                          every document's supplier box. */}
                      <Typeahead
                        label={t('stock.filterSupplier')}
                        value={supplierFilterId}
                        onChange={paging.change(setSupplierFilterId)}
                        options={supplierPicker.options}
                        selected={supplierPicker.optionFor(supplierFilterId ?? '')}
                        query={supplierPicker.query}
                        onQueryChange={supplierPicker.setQuery}
                        emptyLabel={t('picker.noMatchingSupplier')}
                        loading={supplierPicker.loading}
                      />
                    </div>
                  ) : filter === 'tax' ? (
                    <Input
                      aria-label={t('stock.filterTaxCategory')}
                      value={taxCategoryFilter}
                      onChange={(event) =>
                        paging.change(setTaxCategoryFilter)(event.target.value)
                      }
                      maxLength={60}
                      className="h-10 w-full max-w-[220px] text-sm"
                    />
                  ) : (
                    <NativeSelect
                      aria-label={t('stock.filterUom')}
                      value={unitFilterId}
                      onChange={(event) => paging.change(setUnitFilterId)(event.target.value)}
                      className="h-10 w-auto text-sm"
                    >
                      <option value="">{t('stock.filterUomAll')}</option>
                      {(units ?? []).map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                  <button
                    type="button"
                    aria-label={t('stock.removeFilter', { name: t(ADDED_FILTER_LABEL[filter]) })}
                    onClick={() => removeFilter(filter)}
                    className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
                  >
                    <X className="size-4" aria-hidden />
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('stock.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>{t('stock.colCode')}</TableHead>
                  <TableHead>{t('stock.colItem')}</TableHead>
                  <TableHead>{t('stock.colCategory')}</TableHead>
                  <TableHead>{t('stock.colUom')}</TableHead>
                  <TableHead className="text-end">{t('stock.colOnHand')}</TableHead>
                  <TableHead className="text-end">{t('stock.colReserved')}</TableHead>
                  <TableHead className="text-end">{t('stock.colAvailable')}</TableHead>
                  <TableHead className="text-end">{t('stock.colValue')}</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <Fragment key={row.itemId}>
                    <TableRow className={cn('odd:bg-muted/30', stockRowWash(row.status))}>
                      <TableCell>
                        <button
                          type="button"
                          aria-expanded={expanded === row.itemId}
                          aria-label={t(expanded === row.itemId ? 'stock.collapse' : 'stock.expand')}
                          onClick={() =>
                            setExpanded((current) => (current === row.itemId ? null : row.itemId))
                          }
                          className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
                        >
                          {expanded === row.itemId ? (
                            <ChevronDown className="size-4" aria-hidden />
                          ) : (
                            <ChevronRight className="size-4" aria-hidden />
                          )}
                        </button>
                      </TableCell>
                      <TableCell>
                        <Ltr value={row.itemCode} className="font-mono text-sm" />
                      </TableCell>
                      <TableCell>
                        <span className="flex flex-wrap items-center gap-2">
                          {/* S5: the Arabic twin on an Arabic screen, the name otherwise. */}
                          <span className="font-medium">
                            {localizedName(
                              { name: row.itemName, nameAr: row.itemNameAr },
                              i18n.language,
                            )}
                          </span>
                          <StockStatusBadge status={row.status} />
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{row.categoryName}</TableCell>
                      {/*
                        N-4's "next to them", per ROW. INV-05 §5.2 gives the
                        list a Unit column, and it sits immediately before the
                        three quantity columns on the SAME row — which is what
                        the rule asks for and what N-14 needs ("a quantity on a
                        row must always be shown with its unit"). Repeating the
                        symbol on each of On hand / Reserved / Available would
                        add noise and no information. The expander below has no
                        such column, so its figures carry the symbol
                        themselves.
                      */}
                      <TableCell className="text-sm text-muted-foreground">
                        <Ltr value={row.unitSymbol} />
                      </TableCell>
                      <TableCell className="text-end">
                        {/* N-4: the unit's decimals. The SYMBOL is the Unit column
                            beside these three — see the note on that column. */}
                        <Ltr value={formatQty(row.onHand, row.unitPrecision)} className="font-mono" />
                      </TableCell>
                      <TableCell className="text-end">
                        <Ltr
                          value={formatQty(row.reserved, row.unitPrecision)}
                          className="font-mono text-muted-foreground"
                        />
                      </TableCell>
                      <TableCell className="text-end">
                        {/* available = on hand − reserved (BR-INV-05-04). */}
                        <Ltr
                          value={formatQty(row.available, row.unitPrecision)}
                          className={cn('font-mono font-semibold', availableClass(row.status))}
                        />
                      </TableCell>
                      <TableCell className="text-end">
                        <Amount value={row.stockValue} currency={baseCurrency} />
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          aria-label={t('stock.history')}
                          title={t('stock.history')}
                          onClick={() =>
                            setHistory({
                              id: row.itemId,
                              name: localizedName(
                                { name: row.itemName, nameAr: row.itemNameAr },
                                i18n.language,
                              ),
                            })
                          }
                          className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
                        >
                          <Clock className="size-4" aria-hidden />
                        </button>
                      </TableCell>
                    </TableRow>
                    {expanded === row.itemId ? (
                      <TableRow>
                        <TableCell colSpan={10} className="p-0">
                          <Breakdown row={row} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}

          {/* NFR-42 — the range, the total, and the two steps. */}
          {!isPending && !isError && rows.length > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t('stock.title')}
              onLeave={() => setExpanded(null)}
            />
          ) : null}
        </CardContent>
      </Card>

      {history ? (
        <HistoryModal
          itemId={history.id}
          itemName={history.name}
          onClose={() => setHistory(null)}
        />
      ) : null}
    </div>
  );
}
